import React, { useState, useEffect } from 'react';
import { collection, doc, setDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface ConfigureTermsProps {
  domain: string;
}

interface Term {
  id: string;
  termName: string;
  termType: string;
  className: string;
  academicYear: string;
  startDate: string;
  endDate: string;
  weightage: number;
  isActive: boolean;
  createdAt: string;
}

const ConfigureTerms: React.FC<ConfigureTermsProps> = ({ domain }) => {
  const [terms, setTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    termName: '',
    termType: 'Unit Test I',
    className: '1',
    academicYear: '2025-2026',
    startDate: '',
    endDate: '',
    weightage: 10,
    isActive: true,
  });

  const termTypes = [
    'Unit Test I',
    'Unit Test II',
    'Unit Test III',
    'Mid Term',
    'Term I',
    'Term II',
    'Final Exam',
    'Quarterly',
    'Half Yearly',
    'Annual',
  ];

  const academicYears = ['2024-2025', '2025-2026', '2026-2027'];

  useEffect(() => {
    loadTerms();
  }, [domain]);

  const loadTerms = async () => {
    setLoading(true);
    try {
      const termsRef = collection(db, `school-data/${domain}/terms`);
      const snapshot = await getDocs(termsRef);

      const termsList: Term[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Term));

      // Sort by class and term type
      termsList.sort((a, b) => {
        if (a.className !== b.className) {
          return parseInt(a.className) - parseInt(b.className);
        }
        return a.termType.localeCompare(b.termType);
      });

      setTerms(termsList);
    } catch (error: any) {
      console.error('Error loading terms:', error);
      alert(`Failed to load terms: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const termId = `${formData.termType.toLowerCase().replace(/\s+/g, '_')}_${formData.className}_${formData.academicYear.replace('-', '_')}`;

      const termData = {
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, `school-data/${domain}/terms`, termId), termData);

      alert('Term configuration saved successfully!');
      setShowAddForm(false);
      setFormData({
        termName: '',
        termType: 'Unit Test I',
        className: '1',
        academicYear: '2025-2026',
        startDate: '',
        endDate: '',
        weightage: 10,
        isActive: true,
      });
      loadTerms();
    } catch (error: any) {
      console.error('Error saving term:', error);
      alert(`Failed to save term: ${error.message}`);
    }
  };

  const handleDelete = async (termId: string) => {
    if (!confirm('Are you sure you want to delete this term configuration?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, `school-data/${domain}/terms`, termId));
      alert('Term configuration deleted successfully!');
      loadTerms();
    } catch (error: any) {
      console.error('Error deleting term:', error);
      alert(`Failed to delete term: ${error.message}`);
    }
  };

  const toggleActive = async (term: Term) => {
    try {
      await setDoc(
        doc(db, `school-data/${domain}/terms`, term.id),
        { ...term, isActive: !term.isActive, updatedAt: new Date().toISOString() }
      );
      loadTerms();
    } catch (error: any) {
      console.error('Error updating term:', error);
      alert(`Failed to update term: ${error.message}`);
    }
  };

  const downloadExcelTemplate = () => {
    const templateData = [
      {
        'Term Name': 'First Unit Test',
        'Term Type': 'Unit Test I',
        'Class': '10',
        'Academic Year': '2025-2026',
        'Start Date': '2025-04-01',
        'End Date': '2025-04-15',
        'Weightage (%)': 10,
        'Active': 'Yes',
      },
      {
        'Term Name': 'Mid Term Examination',
        'Term Type': 'Mid Term',
        'Class': '10',
        'Academic Year': '2025-2026',
        'Start Date': '2025-07-01',
        'End Date': '2025-07-15',
        'Weightage (%)': 30,
        'Active': 'Yes',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Terms');

    XLSX.writeFile(wb, 'terms_template.xlsx');
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        alert('No data found in Excel file!');
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const row of jsonData) {
        try {
          const termType = row['Term Type'] || '';
          const className = String(row['Class'] || '');
          const academicYear = row['Academic Year'] || '';

          if (!termType || !className || !academicYear) {
            errorCount++;
            continue;
          }

          const termId = `${termType.toLowerCase().replace(/\s+/g, '_')}_${className}_${academicYear.replace('-', '_')}`;

          const termData = {
            termName: row['Term Name'] || termType,
            termType,
            className,
            academicYear,
            startDate: row['Start Date'] || '',
            endDate: row['End Date'] || '',
            weightage: Number(row['Weightage (%)']) || 10,
            isActive: String(row['Active']).toLowerCase() === 'yes' || String(row['Active']).toLowerCase() === 'true',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          await setDoc(doc(db, `school-data/${domain}/terms`, termId), termData);
          successCount++;
        } catch (error) {
          console.error('Error processing row:', row, error);
          errorCount++;
        }
      }

      alert(
        `Excel upload complete!\n\nSuccess: ${successCount} terms\nFailed: ${errorCount} terms`
      );

      loadTerms();

      // Reset file input
      event.target.value = '';
    } catch (error: any) {
      console.error('Error uploading Excel:', error);
      alert(`Failed to upload Excel file: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-calendar-range me-2 text-primary-custom"></i>
              Configure Exam Terms
            </h5>
            <small className="text-muted">Set up exam structure for each class</small>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {showAddForm ? 'Cancel' : 'Add Term'}
          </button>
        </div>

        {/* Excel Upload/Download Buttons */}
        <div className="row g-2">
          <div className="col-md-6">
            <button
              className="btn btn-outline-success btn-sm w-100"
              onClick={downloadExcelTemplate}
            >
              <i className="bi bi-download me-2"></i>
              Download Excel Template
            </button>
          </div>
          <div className="col-md-6">
            <label className="btn btn-outline-primary btn-sm w-100" style={{ cursor: 'pointer' }}>
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  Upload Excel (Bulk)
                </>
              )}
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="card mb-4" style={{ backgroundColor: '#f8f4ff' }}>
            <div className="card-body">
              <h6 className="mb-3">
                <i className="bi bi-plus-square me-2"></i>
                Add New Term Configuration
              </h6>
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Term Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.termName}
                      onChange={(e) => handleInputChange('termName', e.target.value)}
                      placeholder="e.g., First Unit Test"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Term Type *</label>
                    <select
                      className="form-select"
                      value={formData.termType}
                      onChange={(e) => handleInputChange('termType', e.target.value)}
                      required
                    >
                      {termTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Class *</label>
                    <select
                      className="form-select"
                      value={formData.className}
                      onChange={(e) => handleInputChange('className', e.target.value)}
                      required
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          Class {i + 1}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Academic Year *</label>
                    <select
                      className="form-select"
                      value={formData.academicYear}
                      onChange={(e) => handleInputChange('academicYear', e.target.value)}
                      required
                    >
                      {academicYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Weightage (%) *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.weightage}
                      onChange={(e) => handleInputChange('weightage', parseInt(e.target.value))}
                      min="1"
                      max="100"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                    />
                  </div>

                  <div className="col-md-12">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Active (Available for results upload)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-save me-2"></i>
                    Save Term Configuration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Terms List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading terms...</p>
          </div>
        ) : terms.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-calendar-x display-1 text-muted"></i>
            <p className="mt-3 text-muted">
              No terms configured yet. Click "Add Term" to get started.
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Term Name</th>
                  <th>Type</th>
                  <th>Class</th>
                  <th>Academic Year</th>
                  <th>Duration</th>
                  <th>Weightage</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {terms.map((term) => (
                  <tr key={term.id}>
                    <td>
                      <strong>{term.termName || term.termType}</strong>
                    </td>
                    <td>
                      <span className="badge bg-secondary">{term.termType}</span>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">Class {term.className}</span>
                    </td>
                    <td>{term.academicYear}</td>
                    <td>
                      <small className="text-muted">
                        {term.startDate && term.endDate
                          ? `${term.startDate} to ${term.endDate}`
                          : 'Not set'}
                      </small>
                    </td>
                    <td>
                      <span className="badge bg-primary">{term.weightage}%</span>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${term.isActive ? 'btn-success' : 'btn-secondary'}`}
                        onClick={() => toggleActive(term)}
                      >
                        {term.isActive ? (
                          <>
                            <i className="bi bi-check-circle me-1"></i>Active
                          </>
                        ) : (
                          <>
                            <i className="bi bi-x-circle me-1"></i>Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(term.id)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default ConfigureTerms;
